import * as THREE from "three";
import type { AssetPipeline } from "../core/assets";
import { LANDMARK_LAYOUT } from "./layout";

type HeightFn = (x: number, z: number) => number;

const ASSET_PATHS = {
  admin: "/models/world/kenney-industrial/building-b.glb",
  shed: "/models/world/kenney-industrial/building-h.glb",
  lowPlant: "/models/world/kenney-industrial/building-q.glb",
  hall: "/models/world/kenney-industrial/building-t.glb",
  chimney: "/models/world/kenney-industrial/chimney-large.glb",
  detailTank: "/models/world/kenney-industrial/detail-tank.glb",
} as const;

type IndustrialTemplates = Record<keyof typeof ASSET_PATHS, THREE.Group>;
type Footprint = { x: number; z: number; radius: number };

function rotateOffset(x: number, z: number, rotation: number): { x: number; z: number } {
  return {
    x: x * Math.cos(rotation) - z * Math.sin(rotation),
    z: x * Math.sin(rotation) + z * Math.cos(rotation),
  };
}

function tintScene(scene: THREE.Group, brightness = 0, saturation = 0, hue = 0): void {
  scene.traverse((obj) => {
    if (!(obj as THREE.Mesh).isMesh) return;
    const mesh = obj as THREE.Mesh;
    const materials = Array.isArray(mesh.material) ? mesh.material : [mesh.material];
    for (const material of materials) {
      if (!(material as THREE.MeshStandardMaterial).isMeshStandardMaterial) continue;
      const standard = material as THREE.MeshStandardMaterial;
      standard.roughness = Math.max(0.45, standard.roughness ?? 0.7);
      standard.metalness = Math.min(0.35, standard.metalness ?? 0.05);
      standard.color.offsetHSL(hue, saturation, brightness);
      standard.emissive = new THREE.Color(0x0c0c10);
      standard.emissiveIntensity = 0.12;
    }
  });
}

function getTemplateRadius(template: THREE.Group, scale: number, padding = 1.1): number {
  const bbox = new THREE.Box3().setFromObject(template);
  const size = bbox.getSize(new THREE.Vector3());
  return Math.max(size.x, size.z) * scale * padding * 0.5;
}

function overlaps(footprints: Footprint[], x: number, z: number, radius: number): boolean {
  return footprints.some((entry) => Math.hypot(entry.x - x, entry.z - z) < entry.radius + radius);
}

function placeTemplate(
  group: THREE.Group,
  template: THREE.Group,
  assets: AssetPipeline,
  getHeight: HeightFn,
  x: number,
  z: number,
  rotation: number,
  scale: number,
  yOffset = 0,
  tint: { brightness?: number; saturation?: number; hue?: number } = {},
): void {
  const instance = assets.cloneSceneTemplate(template);
  tintScene(instance, tint.brightness ?? 0, tint.saturation ?? 0, tint.hue ?? 0);
  const bbox = new THREE.Box3().setFromObject(instance);
  const minY = Number.isFinite(bbox.min.y) ? bbox.min.y : 0;
  instance.position.set(x, getHeight(x, z) - minY * scale + yOffset, z);
  instance.rotation.y = rotation;
  instance.scale.setScalar(scale);
  instance.traverse((obj) => {
    obj.userData["ignoreBallistics"] = true;
  });
  group.add(instance);
}

async function loadTemplates(assets: AssetPipeline): Promise<IndustrialTemplates> {
  const entries = await Promise.all(
    Object.entries(ASSET_PATHS).map(async ([key, path]) => [key, await assets.loadSceneTemplate(path)] as const),
  );
  return Object.fromEntries(entries) as IndustrialTemplates;
}

function addAssetBackedHangars(group: THREE.Group, templates: IndustrialTemplates, assets: AssetPipeline, getHeight: HeightFn, occupied: Footprint[]): void {
  for (const hangar of LANDMARK_LAYOUT.hangars) {
    const mainRadius = getTemplateRadius(templates.shed, 9.5, 1.02);
    placeTemplate(group, templates.shed, assets, getHeight, hangar.x, hangar.z, hangar.rotation, 9.5, 0.05, { brightness: -0.02, saturation: -0.02 });
    occupied.push({ x: hangar.x, z: hangar.z, radius: mainRadius });

    const annex = rotateOffset(-15, 7, hangar.rotation);
    const annexX = hangar.x + annex.x;
    const annexZ = hangar.z + annex.z;
    const annexRadius = getTemplateRadius(templates.lowPlant, 7.6);
    if (!overlaps(occupied, annexX, annexZ, annexRadius)) {
      placeTemplate(group, templates.lowPlant, assets, getHeight, annexX, annexZ, hangar.rotation + 0.04, 7.6, 0.04, { brightness: -0.03 });
      occupied.push({ x: annexX, z: annexZ, radius: annexRadius });
    }

    const admin = rotateOffset(16, -8.5, hangar.rotation);
    const adminX = hangar.x + admin.x;
    const adminZ = hangar.z + admin.z;
    const adminRadius = getTemplateRadius(templates.admin, 7.1);
    if (!overlaps(occupied, adminX, adminZ, adminRadius)) {
      placeTemplate(group, templates.admin, assets, getHeight, adminX, adminZ, hangar.rotation - 0.08, 7.1, 0.04, { brightness: -0.01, saturation: -0.03 });
      occupied.push({ x: adminX, z: adminZ, radius: adminRadius });
    }

    const stack = rotateOffset(-6, -12, hangar.rotation);
    const stackX = hangar.x + stack.x;
    const stackZ = hangar.z + stack.z;
    const stackRadius = getTemplateRadius(templates.detailTank, 2.3);
    if (!overlaps(occupied, stackX, stackZ, stackRadius)) {
      placeTemplate(group, templates.detailTank, assets, getHeight, stackX, stackZ, hangar.rotation + Math.PI * 0.5, 2.3, 0.02, { brightness: -0.04 });
      occupied.push({ x: stackX, z: stackZ, radius: stackRadius });
    }
  }
}

function addAssetBackedMotorPool(group: THREE.Group, templates: IndustrialTemplates, assets: AssetPipeline, getHeight: HeightFn, occupied: Footprint[]): void {
  const { x, z, rotation } = LANDMARK_LAYOUT.motorPool;
  const hallA = { x: x - 15, z: z - 8, rotation, scale: 8.8 };
  const hallB = { x: x + 15, z: z + 9, rotation: rotation + Math.PI, scale: 8.4 };
  for (const hall of [hallA, hallB]) {
    const radius = getTemplateRadius(templates.hall, hall.scale, 1.04);
    if (!overlaps(occupied, hall.x, hall.z, radius)) {
      placeTemplate(group, templates.hall, assets, getHeight, hall.x, hall.z, hall.rotation, hall.scale, 0.04, { brightness: -0.04, saturation: -0.02 });
      occupied.push({ x: hall.x, z: hall.z, radius });
    }
  }

  const admin = rotateOffset(18, -10, rotation);
  const adminX = x + admin.x;
  const adminZ = z + admin.z;
  const adminRadius = getTemplateRadius(templates.admin, 7.6);
  if (!overlaps(occupied, adminX, adminZ, adminRadius)) {
    placeTemplate(group, templates.admin, assets, getHeight, adminX, adminZ, rotation + 0.2, 7.6, 0.04, { brightness: -0.02 });
    occupied.push({ x: adminX, z: adminZ, radius: adminRadius });
  }

  const service = rotateOffset(19, 11, rotation);
  const serviceX = x + service.x;
  const serviceZ = z + service.z;
  const serviceRadius = getTemplateRadius(templates.lowPlant, 6.8);
  if (!overlaps(occupied, serviceX, serviceZ, serviceRadius)) {
    placeTemplate(group, templates.lowPlant, assets, getHeight, serviceX, serviceZ, rotation + 0.08, 6.8, 0.04, { brightness: -0.04 });
    occupied.push({ x: serviceX, z: serviceZ, radius: serviceRadius });
  }
}

function addAssetBackedStorageYard(group: THREE.Group, templates: IndustrialTemplates, assets: AssetPipeline, getHeight: HeightFn, occupied: Footprint[]): void {
  const { x, z } = LANDMARK_LAYOUT.storageTanks;
  const placements = [
    { template: templates.admin, x: x - 20, z: z + 3, rotation: -0.08, scale: 6.9, tint: { brightness: -0.04 } },
    { template: templates.chimney, x: x + 22, z: z - 13, rotation: 0, scale: 5.6, tint: { brightness: -0.04 } },
    { template: templates.detailTank, x: x + 16, z: z + 14, rotation: 0.2, scale: 2.6, tint: { brightness: -0.05 } },
  ] as const;
  for (const placement of placements) {
    const radius = getTemplateRadius(placement.template, placement.scale);
    if (!overlaps(occupied, placement.x, placement.z, radius)) {
      placeTemplate(group, placement.template, assets, getHeight, placement.x, placement.z, placement.rotation, placement.scale, 0.02, placement.tint);
      occupied.push({ x: placement.x, z: placement.z, radius });
    }
  }
}

export async function addIndustrialAssetSites(group: THREE.Group, assets: AssetPipeline, getHeight: HeightFn): Promise<void> {
  const templates = await loadTemplates(assets);
  const assetGroup = new THREE.Group();
  assetGroup.name = "industrial-asset-sites";
  const occupied: Footprint[] = [];
  addAssetBackedHangars(assetGroup, templates, assets, getHeight, occupied);
  addAssetBackedMotorPool(assetGroup, templates, assets, getHeight, occupied);
  addAssetBackedStorageYard(assetGroup, templates, assets, getHeight, occupied);
  group.add(assetGroup);
}
